import { useAuth } from "@dl/web-core-js/auth";
import { Button, Dialog, Field, Input, toast } from "@dl/web-core-js/ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LotDocumentClient, saveDownload } from "../../api/documents";
import { refusalMessage } from "../../api/manufacturing";
import { PartPicker } from "../manufacturing/PartPicker";

/** The part a cut list is made for. */
export interface CutlistPart {
  id: number;
  number: string;
  name?: string | null;
}

/**
 * A Cut Rite cut list for any part at any quantity, with no production lot:
 * the design office's "how many panels for a new SKU", or the plant's "what
 * would X units take". The part's active bill of materials is exploded; the
 * quantity left empty is the shop-floor default (100).
 */
export function CutlistDialog({ part: given, onClose }: { part?: CutlistPart | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const documents = useMemo(() => new LotDocumentClient(http), [http]);
  const [part, setPart] = useState<CutlistPart | null>(given ?? null);
  const [quantity, setQuantity] = useState("");
  const [busy, setBusy] = useState(false);
  const amount = quantity.trim() === "" ? undefined : Number(quantity);
  const valid = part != null && (amount === undefined || (Number.isFinite(amount) && amount > 0));

  const download = async () => {
    if (!part) return;
    setBusy(true);
    try {
      await saveDownload(() => documents.partCutlist(part.id, amount));
      onClose();
    } catch (failure) {
      toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("cutlist.dialog.title", "Cut list for a part")}
      description={t(
        "cutlist.dialog.description",
        "The panels its active bill of materials needs, as Cut Rite imports them. No production lot is involved.",
      )}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={busy} onClick={() => void download()}>
            {t("cutlist.dialog.download", "Download cut list")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("cutlist.dialog.part", "Part or product")} htmlFor="cutlist-part" required>
          {part ? (
            <div className="flex items-center gap-2 text-[13px]">
              <span className="font-medium">{part.number}</span>
              <span className="text-ink-muted">{part.name}</span>
              {!given && (
                <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setPart(null)}>
                  {t("button.change", "Change")}
                </Button>
              )}
            </div>
          ) : (
            <PartPicker
              onPick={(picked) => setPart({ id: picked.id, number: picked.number, name: picked.name })}
              placeholder={t("part.picker.placeholder", "Search a part by number or name")}
              autoFocus
            />
          )}
        </Field>
        <Field
          label={t("cutlist.dialog.quantity", "Units to build")}
          htmlFor="cutlist-quantity"
          hint={t("cutlist.dialog.quantity.hint", "Empty: the shop-floor default (100).")}
          error={valid || part == null ? undefined : t("cutlist.dialog.quantity.invalid", "Enter a quantity above zero, or leave it empty.")}
        >
          <Input
            id="cutlist-quantity"
            type="number"
            min="1"
            step="1"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
